/**
 * Session WebRTC entre deux navigateurs.
 *
 * ## Répartition des rôles
 *
 * L'émetteur crée le canal de données et émet l'offre ; le récepteur répond. Cette
 * asymétrie est volontaire : elle supprime le cas de la « collision d'offres » (glare),
 * qui impose sinon une négociation parfaite et son lot de cas limites. Le rôle est connu
 * dès l'entrée en salle, on en tire parti.
 *
 * ## Deux pièges traités ici
 *
 * 1. **Candidats ICE arrivant avant la description distante.** `addIceCandidate` lève si
 *    `setRemoteDescription` n'a pas encore été appelée. Or les candidats et la description
 *    transitent par deux chemins indépendants : l'ordre n'est pas garanti. Ils sont donc
 *    mis en attente puis rejoués.
 * 2. **Absence de chemin réseau.** Sans échéance explicite, l'API reste en `checking`
 *    indéfiniment et l'utilisateur voit une barre bloquée à 0 %. On déclare l'échec au
 *    bout de `ICE_FAILURE_TIMEOUT_MS` avec un message exploitable.
 */

import {
  ICE_FAILURE_TIMEOUT_MS,
  type IceCandidatePayload,
  type IceServer,
  type Role,
  type SdpDescription,
  type SignalPayload,
} from '@attrape/shared';
import { msg, type MessageDescriptor } from '../i18n/index.js';
import { DataChannelTransport, type Transport } from './transport.js';

/**
 * Nombre de tentatives de lecture du chemin réseau, et intervalle entre elles.
 *
 * 10 × 500 ms ≈ 5 s : assez pour laisser ICE désigner son couple de candidats définitif,
 * assez court pour ne pas retarder le diagnostic en cas d'échec réel.
 */
const MAX_CONNECTION_INFO_ATTEMPTS = 10;
const CONNECTION_INFO_RETRY_MS = 500;

/** Types de candidat ICE, du plus direct au plus indirect. */
export type CandidateType = 'host' | 'srflx' | 'prflx' | 'relay' | 'unknown';

export interface ConnectionInfo {
  candidateType: CandidateType;
  /** Protocole du chemin retenu (`udp` ou `tcp`). */
  protocol: string | null;
  /** `true` si les données transitent par un serveur TURN plutôt qu'en direct. */
  throughServer: boolean;
  state: RTCPeerConnectionState;
}

export interface PeerSessionOptions {
  role: Role;
  iceServers: IceServer[];
  sendSignal: (payload: SignalPayload) => void;
  onTransportReady: (transport: Transport) => void;
  onConnectionInfo: (info: ConnectionInfo) => void;
  /**
   * Signalé quand aucun chemin réseau ne peut aboutir.
   *
   * Le motif est un descripteur de message : `peer.ts` ne connaît pas la langue de
   * l'utilisateur et n'a pas à la connaître.
   */
  onFailure: (reason: MessageDescriptor) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lecture défensive des statistiques WebRTC
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `getStats()` renvoie une carte de rapports dont le type précis n'est pas garanti par le
 * DOM. On décode donc à la frontière plutôt que de transtyper en aveugle : un rapport
 * inattendu produit `null`, pas un `undefined` propagé trois fonctions plus loin.
 */
interface CandidatePairStats {
  type: 'candidate-pair';
  id?: string;
  state?: string;
  nominated?: boolean;
  localCandidateId?: string;
}

interface TransportStats {
  type: 'transport';
  selectedCandidatePairId?: string;
}

interface LocalCandidateStats {
  type: 'local-candidate';
  candidateType?: string;
  protocol?: string;
}

function isCandidatePair(report: unknown): report is CandidatePairStats {
  return (
    typeof report === 'object' &&
    report !== null &&
    (report as { type?: unknown }).type === 'candidate-pair'
  );
}

function isTransport(report: unknown): report is TransportStats {
  return (
    typeof report === 'object' &&
    report !== null &&
    (report as { type?: unknown }).type === 'transport'
  );
}

function isLocalCandidate(report: unknown): report is LocalCandidateStats {
  return (
    typeof report === 'object' &&
    report !== null &&
    (report as { type?: unknown }).type === 'local-candidate'
  );
}

function normalizeCandidateType(value: string | undefined): CandidateType {
  if (value === 'host' || value === 'srflx' || value === 'prflx' || value === 'relay') {
    return value;
  }
  return 'unknown';
}

/** Identifie le chemin réseau réellement retenu par ICE. */
export async function readConnectionInfo(pc: RTCPeerConnection): Promise<ConnectionInfo> {
  const base: ConnectionInfo = {
    candidateType: 'unknown',
    protocol: null,
    throughServer: false,
    state: pc.connectionState,
  };

  try {
    const stats = await pc.getStats();

    // Deux sources possibles, par ordre de fiabilité décroissante :
    //
    // 1. `transport.selectedCandidatePairId` — désigne explicitement le couple retenu.
    //    C'est la source normative, mais tous les moteurs ne l'exposent pas.
    // 2. Balayage des couples à la recherche de celui marqué `nominated` et `succeeded`.
    //    Repli nécessaire : sans lui, le diagnostic resterait indéterminé sur certains
    //    navigateurs et l'utilisateur ne saurait jamais si ses données passent en direct.
    let selectedPairId: string | null = null;
    for (const report of stats.values()) {
      if (isTransport(report) && typeof report.selectedCandidatePairId === 'string') {
        selectedPairId = report.selectedCandidatePairId;
        break;
      }
    }

    let selectedPair: CandidatePairStats | null = null;
    if (selectedPairId) {
      const report = stats.get(selectedPairId);
      selectedPair = isCandidatePair(report) ? report : null;
    }
    if (!selectedPair) {
      for (const report of stats.values()) {
        if (!isCandidatePair(report)) continue;
        // Un couple « nominated » et « succeeded » est celui qui transporte réellement
        // les données ; les autres sont des candidats explorés puis abandonnés.
        if (report.nominated === true && report.state === 'succeeded') {
          selectedPair = report;
          break;
        }
      }
    }

    const localCandidateId = selectedPair?.localCandidateId;
    if (!localCandidateId) return base;

    const localReport = stats.get(localCandidateId);
    if (!isLocalCandidate(localReport)) return base;

    const candidateType = normalizeCandidateType(localReport.candidateType);
    return {
      candidateType,
      protocol: typeof localReport.protocol === 'string' ? localReport.protocol : null,
      throughServer: candidateType === 'relay',
      state: pc.connectionState,
    };
  } catch {
    // Les statistiques sont un confort de diagnostic : leur indisponibilité ne doit
    // jamais faire échouer un transfert.
    return base;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convertit un `RTCIceCandidate` en charge utile de protocole.
 *
 * `toJSON()` renvoie un `candidate` **optionnel**, ce qui rendrait le message ambigu :
 * un objet vide serait indistinguable de la fin de collecte. On construit donc l'objet
 * explicitement, en omettant les champs absents plutôt qu'en les laissant `undefined`.
 */
export function serializeCandidate(candidate: RTCIceCandidate): IceCandidatePayload | null {
  const raw = candidate.candidate;
  if (!raw) return null;

  const payload: IceCandidatePayload = { candidate: raw };
  if (candidate.sdpMid !== null && candidate.sdpMid !== undefined) {
    payload.sdpMid = candidate.sdpMid;
  }
  if (typeof candidate.sdpMLineIndex === 'number') {
    payload.sdpMLineIndex = candidate.sdpMLineIndex;
  }
  if (candidate.usernameFragment) {
    payload.usernameFragment = candidate.usernameFragment;
  }
  return payload;
}

export class PeerSession {
  private readonly pc: RTCPeerConnection;
  private transport: DataChannelTransport | null = null;
  private failureTimer: ReturnType<typeof setTimeout> | null = null;
  private settled = false;
  private remoteDescriptionApplied = false;
  private readonly pendingCandidates: (IceCandidatePayload | null)[] = [];

  constructor(private readonly options: PeerSessionOptions) {
    this.pc = new RTCPeerConnection({
      iceServers: options.iceServers,
      // `all` : on laisse ICE trouver le chemin le plus court. Forcer `relay` serait
      // plus déterministe mais priverait tout le monde de la vitesse du direct.
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
    });

    this.wireConnection();
  }

  private wireConnection(): void {
    const { pc } = this;

    pc.onicecandidate = (event) => {
      // `null` marque la fin de la collecte : on la transmet pour que le pair sache
      // qu'aucun candidat supplémentaire n'arrivera.
      this.options.sendSignal({
        kind: 'ice',
        candidate: event.candidate ? serializeCandidate(event.candidate) : null,
      });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        this.clearFailureTimer();
        this.pollConnectionInfo();
      }
      if (pc.connectionState === 'failed') {
        this.fail(msg.key('errors.connectionFailed'));
      }
    };

    // Le récepteur reçoit le canal par ce chemin : il ne le crée pas.
    pc.ondatachannel = (event) => {
      this.attachChannel(event.channel);
    };
  }

  private attachChannel(channel: RTCDataChannel): void {
    channel.binaryType = 'arraybuffer';

    channel.onopen = () => {
      this.clearFailureTimer();
      this.transport = new DataChannelTransport(channel);
      this.pollConnectionInfo();
      this.options.onTransportReady(this.transport);
    };

    channel.onclose = () => {
      // Une fermeture après un échec déjà signalé ne doit pas produire un second message.
      if (this.settled) return;
    };

    channel.onerror = () => {
      this.fail(msg.key('errors.dataChannel'));
    };
  }

  /**
   * Remonte le chemin réseau retenu, en réessayant tant qu'il reste indéterminé.
   *
   * Une lecture unique ne suffit pas : à l'ouverture du canal, ICE n'a pas toujours désigné
   * son couple de candidats définitif et `getStats` ne renvoie alors qu'un type indéterminé.
   * Sans nouvelle tentative, l'utilisateur resterait bloqué sur « Établissement du chemin… »
   * alors que la connexion fonctionne — or c'est précisément l'information qui distingue un
   * transfert direct d'un transfert relayé, et le seul moyen de vérifier la promesse du
   * produit.
   *
   * Les tentatives sont bornées : au-delà, si ICE n'a rien trouvé, c'est le délai d'échec
   * qui conclut, avec un message exploitable.
   */
  private pollConnectionInfo(): void {
    let attempts = 0;

    const attempt = async (): Promise<void> => {
      if (this.settled || this.pc.connectionState === 'closed') return;

      const info = await readConnectionInfo(this.pc);
      this.options.onConnectionInfo(info);

      if (info.candidateType !== 'unknown') return;
      if (this.pc.connectionState === 'failed') return;

      attempts += 1;
      if (attempts >= MAX_CONNECTION_INFO_ATTEMPTS) return;
      setTimeout(() => void attempt(), CONNECTION_INFO_RETRY_MS);
    };

    void attempt();
  }

  /** Démarre la négociation côté émetteur : crée le canal puis émet l'offre. */
  async start(): Promise<void> {
    const channel = this.pc.createDataChannel('transfer', {
      // Fiable et ordonné : c'est ce qui permet au récepteur d'exiger une séquence
      // strictement croissante et de détecter tout rejeu ou toute réorganisation.
      ordered: true,
    });
    this.attachChannel(channel);

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.options.sendSignal({
      kind: 'sdp',
      description: { type: 'offer', sdp: offer.sdp ?? '' },
    });
    this.armFailureTimer();
  }

  /** Traite un message de signaling reçu du pair distant. */
  async handleSignal(payload: SignalPayload): Promise<void> {
    if (payload.kind === 'sdp') {
      await this.handleDescription(payload.description);
      return;
    }
    await this.handleCandidate(payload.candidate);
  }

  private async handleDescription(description: SdpDescription): Promise<void> {
    if (description.type !== 'offer' && description.type !== 'answer') return;
    if (!description.sdp) {
      this.fail(msg.raw('Description de session vide reçue du pair.'));
      return;
    }

    try {
      await this.pc.setRemoteDescription({ type: description.type, sdp: description.sdp });
      this.remoteDescriptionApplied = true;
      await this.flushPendingCandidates();
    } catch (error) {
      this.fail(msg.raw(`Description de session refusée : ${String(error)}`));
      return;
    }

    if (description.type === 'offer') {
      try {
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.options.sendSignal({
          kind: 'sdp',
          description: { type: 'answer', sdp: answer.sdp ?? '' },
        });
        this.armFailureTimer();
      } catch (error) {
        this.fail(msg.raw(`Impossible de répondre à la négociation : ${String(error)}`));
      }
    }
  }

  private async handleCandidate(candidate: IceCandidatePayload | null): Promise<void> {
    // Tant que la description distante n'est pas appliquée, `addIceCandidate` lève.
    // Les candidats arrivent souvent en avance : on les met de côté.
    if (!this.remoteDescriptionApplied) {
      this.pendingCandidates.push(candidate);
      return;
    }

    try {
      if (candidate === null) {
        await this.pc.addIceCandidate();
        return;
      }
      await this.pc.addIceCandidate(candidate);
    } catch (error) {
      // Un candidat isolé peut être rejeté sans que la session soit perdue : ICE en
      // essaie d'autres. On ne déclare donc pas l'échec ici.
      if (import.meta.env.DEV) {
        console.warn('Candidat ICE rejeté', error);
      }
    }
  }

  private async flushPendingCandidates(): Promise<void> {
    const queued = this.pendingCandidates.splice(0);
    for (const candidate of queued) {
      await this.handleCandidate(candidate);
    }
  }

  private armFailureTimer(): void {
    this.clearFailureTimer();
    this.failureTimer = setTimeout(() => {
      if (this.pc.connectionState === 'connected' || this.settled) return;
      this.fail(msg.key('errors.iceFailed'));
    }, ICE_FAILURE_TIMEOUT_MS);
  }

  private clearFailureTimer(): void {
    if (this.failureTimer === null) return;
    clearTimeout(this.failureTimer);
    this.failureTimer = null;
  }

  private fail(reason: MessageDescriptor): void {
    if (this.settled) return;
    this.settled = true;
    this.clearFailureTimer();
    this.options.onFailure(reason);
  }

  close(): void {
    this.settled = true;
    this.clearFailureTimer();
    this.transport?.close();
    this.pc.onicecandidate = null;
    this.pc.onconnectionstatechange = null;
    this.pc.ondatachannel = null;
    this.pc.close();
  }
}
